<template>
  <div class="page-container">
    <main class="main-content">
      <div class="page-inner-box">
        <!-- 面包屑导航 -->
        <el-breadcrumb separator-class="el-icon-arrow-right">
          <el-breadcrumb-item to="/">首页</el-breadcrumb-item>
          <el-breadcrumb-item>会员中心</el-breadcrumb-item>
        </el-breadcrumb>
        
        <!-- Banner Ad Section -->
        <div v-if="bannerAds.length > 0" class="page-inner-box banner-section" style="margin-top: 20px; margin-bottom: 0;">
          <div v-for="ad in bannerAds" :key="ad.id" class="banner-item" @click="openAd(ad)">
            <img :src="sanitizeUrl(ad.adImage)" :alt="ad.adTitle" class="banner-img">
          </div>
        </div>

        <div class="member-center-container">
          <!-- 全局侧边栏组件 -->
          <Sidebar 
            :active-nav="activeNav"
            :current-view="currentView"
            @handle-my-courses="handleMyCourses"
            @switch-to-projects="switchToProjects"
            @switch-to-simulation="switchToSimulation"
            @switch-to-messages="switchToMessages"
            @handle-logout="handleLogout"
          />
          <div class="content-main">
            <router-view />
          </div>
        </div>
      </div>
    </main>

    <!-- Popup Ad -->
    <AdPopup v-if="popupAd" :ad="popupAd" page-id="member_center" />

    <!-- Float Ad -->
    <AdFloat v-if="floatAd" :ad="floatAd" :style="{ right: '20px', bottom: '100px' }" />

  </div>
</template>

<script>
import Sidebar from '@/components/Sidebar.vue'
import AdPopup from '@/components/AdPopup.vue'
import AdFloat from '@/components/AdFloat.vue'
import { mapActions, mapGetters } from 'vuex'
import { getAdvertisementList, addAdvertisementView } from '@/api/courseApi'

export default {
  name: 'MemberCenter',
  components: {
    Sidebar,
    AdPopup,
    AdFloat
  },
  computed: {
    ...mapGetters('auth', ['isLoggedIn', 'userInfo', 'username'])
  },
  data() {
    return {
      activeNav: 'courses',
      currentView: 'courses', // 'courses'/'collections'/'projects'/'simulation'/'messages'/'settings'
      popupAd: null,
      bannerAds: [],
      floatAd: null
    }
  },
  methods: {
    ...mapActions('auth', ['logout']),
    handleMyCourses() {
      // 如果不在会员中心页面，则跳转到会员中心
      if (this.$route.path !== '/member-center') {
        this.$router.push('/member-center').catch(err => {
          if (err.name !== 'NavigationDuplicated') {
            throw err
          }
        })
      } else {
        // 如果已经在会员中心页面，则切换到课程视图
        this.currentView = 'courses'
      }
    },

    switchToProjects() {
      this.$router.push('/member-center/projects')
    },
    switchToSimulation() {
      this.$router.push('/member-center/simulation')
    },
    switchToMessages() {
      this.$router.push('/member-center/messages')
    },
    switchToSettings() {
      this.$router.push('/member-center/settings')
    },
    backToCourses() {
      this.$router.push('/member-center')
    },
    handleLogout() {
      this.logout()
      this.$message({
        message: '退出登录成功',
        type: 'success'
      })
      this.$router.push('/')
    },
    // 检查登录状态，如果未登录则跳转到登录页面
    checkLoginStatus() {
      if (!this.isLoggedIn) {
        this.$message.warning('请先登录再访问会员中心')
        this.$router.push('/')
      }
    },
    sanitizeUrl(val) {
      const s = String(val || '').trim()
      if (!s) return ''
      return s.replace(/\\`/g, '').replace(/`/g, '').trim()
    },
    async fetchAdvertisements() {
      try {
        const res = await getAdvertisementList({
          displayPosition: 'personal_center'
        })
        if (res && res.data) {
          const list = Array.isArray(res.data) ? res.data : []
          const enabled = list.filter(item => Number(item && item.enableStatus) === 1 && Number(item && item.adStatus) === 1)
          
          // Process Popup Ads
          const popupAds = enabled.filter(item => String(item && item.adType) === 'popup')
          if (popupAds.length > 0) {
            this.popupAd = popupAds[0]
          }
          
          // Process Banner Ads
          this.bannerAds = enabled.filter(item => String(item && item.adType) === 'banner')
          
          // Process Float Ads
          const floatAds = enabled.filter(item => String(item && item.adType) === 'float')
          if (floatAds.length > 0) {
            this.floatAd = floatAds[0]
          }
        }
      } catch (error) {
        console.error('Failed to fetch advertisements:', error)
      }
    },
    openAd(ad) {
      if (!ad) return
      // Track view count
      if (ad.id) {
        addAdvertisementView(ad.id).catch(e => console.error('Failed to track ad view', e))
      }
      
      const url = this.sanitizeUrl(ad.linkUrl)
      if (url) {
        window.open(url, '_blank')
      }
    }
  },
  watch: {
    // 监听路由变化，根据查询参数切换视图
    '$route'(to) {
      if (to.query.view === 'messages') {
        this.switchToMessages()
      } else if (to.query.view) {
        this.currentView = to.query.view
      } else {
        // 当路由变化且没有查询参数时，重置currentView
        this.currentView = 'courses'
      }
    }
  },
  // 页面加载时检查登录状态和路由参数
  mounted() {
    this.fetchAdvertisements()
    if (this.$route.query.view) {
      this.currentView = this.$route.query.view
    }
  }
}
</script>

<style scoped lang="scss">
.page-container { min-height: 100vh; display: flex; flex-direction: column; }
.main-content { flex: 1; padding-top: 20px; }

.member-center-container {
  display: flex;
  gap: 24px;
  margin-top: 20px;
}

.content-main {
  flex: 1;
  min-width: 0;
  background: #fff;
  border-radius: 8px;
  padding: 24px;
  // box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  overflow: hidden;

  .top-info {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;

    .user-status {
      display: flex;
      align-items: center;
      gap: 12px;

      .user-name {
        font-size: 16px;
        font-weight: 500;
        color: #333;
      }
    }

    .settings {
      display: flex;
      align-items: center;
      gap: 4px;
      cursor: pointer;
      color: #606266;
      font-size: 14px;

      &:hover {
        color: #409eff;
      }
    }
  }

  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;

    .section-title {
      font-size: 20px;
      font-weight: 600;
      color: #3E2CCD;
    }
  }

  /* 课程列表样式 */
  .courses-list {
    .course-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 20px;
      background: #fafafa;
      border-radius: 6px;
      margin-bottom: 16px;

      .course-info {
        .course-title {
          font-size: 16px;
          font-weight: 500;
          color: #333;
          margin-bottom: 4px;
        }

        .course-desc {
          font-size: 14px;
          color: #999;
        }
      }
    }
  }

  /* 收藏列表样式 */
  .collections-list {
    .collection-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 20px;
      background: #fafafa;
      border-radius: 6px;
      margin-bottom: 16px;

      .collection-info {
        .collection-title {
          font-size: 16px;
          font-weight: 500;
          color: #333;
          margin-bottom: 4px;
        }

        .collection-desc {
          font-size: 14px;
          color: #999;
        }
      }
    }
  }

  /* 项目列表样式 */
  .projects-list {
    .project-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 20px;
      background: #fafafa;
      border-radius: 6px;
      margin-bottom: 16px;

      .project-info {
        .project-title {
          font-size: 16px;
          font-weight: 500;
          color: #333;
          margin-bottom: 4px;
        }

        .project-desc {
          font-size: 14px;
          color: #999;
        }
      }
    }
  }

  /* 模拟练习样式 */
  .simulation-list {
    .simulation-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 20px;
      background: #fafafa;
      border-radius: 6px;
      margin-bottom: 16px;

      .simulation-info {
        .simulation-title {
          font-size: 16px;
          font-weight: 500;
          color: #333;
          margin-bottom: 4px;
        }

        .simulation-desc {
          font-size: 14px;
          color: #999;
        }
      }
    }
  }

  /* 站内信样式 */
  .messages-list {
    .message-item {
      padding: 20px;
      background: #fafafa;
      border-radius: 6px;
      margin-bottom: 16px;

      .message-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 12px;

        .message-title {
          font-size: 16px;
          font-weight: 500;
          color: #333;
        }

        .message-time {
          font-size: 12px;
          color: #999;
        }
      }

      .message-content {
        font-size: 14px;
        color: #606266;
        line-height: 1.6;
      }
    }
  }

  /* 设置内容样式 */
  .settings-content {
    .el-form {
      max-width: 500px;
    }
  }
}

/* Banner Ad Styles */
.banner-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.banner-item {
  width: 100%;
  cursor: pointer;
  border-radius: 8px;
  overflow: hidden;
  height: 120px;
}

.banner-img {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
}
</style>
